// Contact sheet review (see _notes/spec_contact_sheet_review.md): which elements
// are symbols to review, how they group into sections, the order they show in,
// and review progress. Kept apart from the component so it can be tested alone.
import { getLabelForType, EQUIPMENT_TYPE_MAP } from '../data/equipmentTypes';

// Not symbols: text, pipe topology and annotations, reviewed in the drawing view.
export const NOT_SYMBOLS = new Set([
  'text_label', 'drawing_note', 'pipe_junction', 'line_break', 'pipe_end', 'pipe',
  'directional_arrow', 'pipe_gradient', 'relationship', 'node',
]);
export const UNCLASSIFIED = 'ml_symbol';

export const isSymbol = (el) => !NOT_SYMBOLS.has(el.elementType || 'node') && !el.metadata?.rejected;

// A retired class (equipmentTypes.js retiredTo) shows, and is confirmed, as the
// class it became.
export const currentType = (type) => EQUIPMENT_TYPE_MAP[type]?.retiredTo || type;
const isRetired = (el) => currentType(el.elementType) !== el.elementType;

// A full confirm vouches for the class too — but not for a retired class, which
// still needs moving to its new one.
export const isTypeConfirmed = (el) => Boolean(el.typeConfirmed || el.confirmed) && !isRetired(el);

export const isInstrument = (el) => EQUIPMENT_TYPE_MAP[currentType(el.elementType)]?.category === 'Instruments';
export const needsReview = (el) => el.instrument?.derived?.reviewStatus === 'needs_review';

/**
 * The bulk calls that confirm these elements' classes: a retired class is moved
 * to the class it became (a class change counts as confirming), the rest just
 * get typeConfirmed.
 */
export function confirmCalls(elements) {
  const calls = new Map();
  for (const el of elements) {
    const to = currentType(el.elementType);
    const key = isRetired(el) ? `type:${to}` : 'confirm';
    if (!calls.has(key)) calls.set(key, { ids: [], set: isRetired(el) ? { elementType: to } : { typeConfirmed: true } });
    calls.get(key).ids.push(String(el._id));
  }
  return [...calls.values()];
}

// Claude's two looks disagreed (the close-up overrode the first answer): the
// likeliest mistakes, kept out of a section's Confirm all.
export const needsSecondLook = (el) => {
  const found = el.metadata?.claude_find_label;
  return Boolean(found && found !== el.elementType);
};

const readingOrder = (a, b) => {
  const ay = a.boundingBox?.y ?? 0; const by = b.boundingBox?.y ?? 0;
  const rowA = Math.round(ay / 200); const rowB = Math.round(by / 200);
  return rowA - rowB || (a.boundingBox?.x ?? 0) - (b.boundingBox?.x ?? 0);
};

/**
 * Sections of the contact sheet, easiest wins first: one per class, each split
 * into `main` (the bulk — Confirm all) and `secondLook` (likely mistakes, at the
 * end), confirmed tiles last in each. Unfinished sections come first, largest
 * first, then the unclassified ones, then finished sections.
 */
export function buildSections(elements) {
  const byType = new Map();
  for (const el of elements) {
    if (!isSymbol(el)) continue;
    const type = currentType(el.elementType);
    if (!byType.has(type)) byType.set(type, []);
    byType.get(type).push(el);
  }
  const sections = [...byType.entries()].map(([type, items]) => {
    const sorted = [...items].sort((a, b) => isTypeConfirmed(a) - isTypeConfirmed(b) || readingOrder(a, b));
    const unclassified = type === UNCLASSIFIED;
    const main = unclassified ? [] : sorted.filter(el => !needsSecondLook(el) || isTypeConfirmed(el));
    const secondLook = unclassified ? sorted : sorted.filter(el => needsSecondLook(el) && !isTypeConfirmed(el));
    const confirmed = items.filter(isTypeConfirmed).length;
    return {
      type, label: getLabelForType(type), category: EQUIPMENT_TYPE_MAP[type]?.category || 'Other',
      unclassified, main, secondLook, total: items.length, confirmed, done: confirmed === items.length,
    };
  });
  const rank = (s) => (s.done ? 2 : s.unclassified ? 1 : 0);
  return sections.sort((a, b) => rank(a) - rank(b) || (b.total - b.confirmed) - (a.total - a.confirmed) || a.label.localeCompare(b.label));
}

export function reviewProgress(elements) {
  const symbols = elements.filter(isSymbol);
  const done = symbols.filter(isTypeConfirmed).length;
  return { done, total: symbols.length, pct: symbols.length ? done / symbols.length : 1 };
}

/**
 * The square window cut around a symbol for its tile (page pixels): the box
 * plus as much again on every side, at least `minSide`, kept on the page.
 */
export function cropWindow(box, imageW, imageH, padRatio = 1, minSide = 48) {
  const side = Math.min(Math.max(Math.max(box.width, box.height) * (1 + 2 * padRatio), minSide), imageW, imageH);
  const cx = box.x + box.width / 2; const cy = box.y + box.height / 2;
  const x = Math.min(Math.max(cx - side / 2, 0), imageW - side);
  const y = Math.min(Math.max(cy - side / 2, 0), imageH - side);
  return { x, y, side };
}

/**
 * The bulk calls that put a class / type-confirmed change back, from the bulk
 * endpoint's `previous` values: elements sharing the same old values go in one
 * call. (The server applies elementType before typeConfirmed, so restoring an
 * old class doesn't leave it marked as checked.)
 */
export function undoCalls(previous) {
  const groups = new Map();
  for (const p of previous) {
    const key = `${p.elementType}|${p.typeConfirmed}`;
    if (!groups.has(key)) groups.set(key, { ids: [], set: { elementType: p.elementType, typeConfirmed: p.typeConfirmed } });
    groups.get(key).ids.push(p._id);
  }
  return [...groups.values()];
}

/** The bulk calls that put instruments' previous line styles back. */
export function lineStyleUndoCalls(previous) {
  const groups = new Map();
  for (const p of previous) {
    if (!p.lineStyle) continue;
    if (!groups.has(p.lineStyle)) groups.set(p.lineStyle, { ids: [], set: { lineStyle: p.lineStyle } });
    groups.get(p.lineStyle).ids.push(p._id);
  }
  return [...groups.values()];
}

export const LINE_STYLES = [
  { value: 'none', short: 'None', label: 'None (field)', glyph: '○' },
  { value: 'solid', short: 'Solid', label: 'Solid (control room)', glyph: '⊖' },
  { value: 'dashed', short: 'Dashed', label: 'Dashed (control room, behind panel)', glyph: '⊝' },
  { value: 'double', short: 'Double', label: 'Double (local panel)', glyph: '⊜' },
  { value: 'double_dashed', short: 'Double dashed', label: 'Double dashed (local panel, behind)', glyph: '≑' },
  { value: 'crossed', short: 'Crossed', label: 'Crossed / diagonal (safety system)', glyph: '⊗' },
  { value: 'unknown', short: 'Unknown', label: 'Unknown', glyph: '?' },
];
